import Loadable from 'react-loadable';

import { LoadingPage, LayoutLoadingPage } from 'src/components/react-loadable';
import { loadGlobalResource } from 'src/redux/global/resources/load-global';
import {
  AccountPaymentLoadable,
  PlanSelectorLoadable,
} from 'src/sections/account';
import { accountDormantWorkspacesPartOfWsCleanupResource } from 'src/sections/account/account-dormant-workspaces-part-of-ws-cleanup-resource';
import { accountNavigationResource } from 'src/sections/account/account-navigation-resource';
import { userWorkspacesResource } from 'src/sections/account/user-workspaces-resource';
import attributionsResource from 'src/sections/global/components/attributions/resource';
import { workspaceNavigationResource } from 'src/sections/workspace/workspace-navigation-resource';

import { ROUTE_NAME, Route, RouteContext } from './types';

const FocusedTaskPageLayout = Loadable({
  loading: LayoutLoadingPage,
  loader: () =>
    import('src/sections/global/components/focused-task-page-layout'),
});

const AccountWorkspacesLayout = Loadable({
  loading: LayoutLoadingPage,
  loader: () => import('src/sections/account/workspaces-layout'),
});

const Search = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () => import('src/sections/search/containers/page'),
});

const AccountWorkspaces = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () => import('src/sections/account/components/workspaces'),
});

const DeactivatedWorkspaces = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () =>
    import('src/sections/account/components/workspaces/deactivated'),
});

const GlobalCreateBranchPage = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () => import('src/sections/create-branch/containers/global-page'),
});

const AttributionsPage = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () => import('src/sections/global/components/attributions/page'),
});

const ERSWorkbenchPage = Loadable<RouteContext, never>({
  loading: LoadingPage,
  loader: () =>
    import('src/sections/global/components/ers-workbench/page').then(
      module => module.ERSWorkbenchPage
    ),
});

export const globalRoutes = (): Route[] => {
  // most "global" routes do not have a workspace in context so we want to use the
  // accountNavigationResource which includes the default workspace menu items
  // and everything we need to render nav4
  const defaultResources = [loadGlobalResource, accountNavigationResource];

  // a few of the "global" routes include a uuid in the path which is the workspace
  // uuid. We can use that uuid in the workspaceNavigationResource (it won't be keyed
  // the same as the slug version but will work to get the menu items)
  const defaultResourcesWithWorkspace = [
    loadGlobalResource,
    workspaceNavigationResource,
  ];

  return [
    {
      name: ROUTE_NAME.GLOBAL_SEARCH,
      path: '/search',
      component: Search,
      layout: FocusedTaskPageLayout,
      resources: defaultResources,
    },
    {
      name: ROUTE_NAME.GLOBAL_ACCOUNT_WORKSPACES,
      path: '/account/workspaces',
      exact: true,
      component: AccountWorkspaces,
      layout: AccountWorkspacesLayout,
      resources: [...defaultResources, userWorkspacesResource],
    },
    {
      name: ROUTE_NAME.GLOBAL_ACCOUNT_DEACTIVATED_WORKSPACES,
      path: '/account/workspaces/deactivated',
      exact: true,
      component: DeactivatedWorkspaces,
      layout: AccountWorkspacesLayout,
      resources: [
        ...defaultResources,
        accountDormantWorkspacesPartOfWsCleanupResource,
      ],
    },
    {
      name: ROUTE_NAME.GLOBAL_ACCOUNT_PLAN_SELECTOR,
      path: '/:workspaceSlug/workspace/settings/plans',
      component: PlanSelectorLoadable,
      layout: FocusedTaskPageLayout,
      resources: defaultResourcesWithWorkspace,
    },
    ...[
      '/account/user/:uuid/payment',
      '/account/user/:uuid/billing-details',
    ].map(path => ({
      name: ROUTE_NAME.GLOBAL_ACCOUNT_PAYMENT,
      path,
      component: AccountPaymentLoadable,
      layout: FocusedTaskPageLayout,
      resources: defaultResourcesWithWorkspace,
    })),
    {
      name: ROUTE_NAME.GLOBAL_BRANCH_CREATE,
      path: '/branch/create',
      component: GlobalCreateBranchPage,
      layout: FocusedTaskPageLayout,
      resources: defaultResources,
    },
    {
      name: ROUTE_NAME.GLOBAL_ATTRIBUTIONS_PAGE,
      path: '/site/attributions',
      component: AttributionsPage,
      layout: FocusedTaskPageLayout,
      resources: [...defaultResources, attributionsResource],
    },
    {
      name: ROUTE_NAME.GLOBAL_ERS_WORKBENCH,
      path: '/support/admin/ers',
      exact: true,
      component: ERSWorkbenchPage,
      layout: FocusedTaskPageLayout,
      resources: defaultResources,
    },
  ];
};
